import React, { useState, useCallback, useEffect } from "react";
import { Button, Spinner } from "../../shared";
import {
  DocumentArrowUpIcon,
  XMarkIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { FileViewer } from "../../shared/FileViewer";
import CSVParser from "./CSVParser";
import PDFReader from "./PDFReader";
import ExcelParser from "./ExcelParser";
import { PRESET_CONFIGS } from "./configs";

/**
 * Main File Processor Component
 *
 * Orchestrates file processing by delegating to specialized parsers
 * based on file type while providing a unified interface.
 *
 * @param {Object} props - Component props
 * @param {Object} props.config - Configuration object for parsing
 * @param {string} props.appType - Application type ("trucking" or "service")
 * @param {Function} props.onDataParsed - Callback when data is successfully parsed
 * @param {Function} props.onError - Callback when parsing fails
 * @param {Function} props.onFileSelect - Callback when file is selected
 * @param {Array} props.acceptedFileTypes - Array of accepted file extensions
 * @param {boolean} props.showPreview - Whether to show data preview
 * @param {boolean} props.showFileViewer - Whether to show file viewer for preview
 * @param {boolean} props.showFileViewerHeader - Whether to show FileViewer header
 * @param {boolean} props.viewerOnly - Whether to show only the viewer, no upload interface
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.children - Custom render function for parsed data
 * @param {File} props.selectedFile - Selected file for processing
 * @param {Function} props.onSheetsParsed - Callback with the Excel sheet grid used by the preview
 * @param {boolean} props.autoProcess - Whether to automatically process the file
 */
const FileProcessor = ({
  config = {},
  appType = "service",
  onDataParsed,
  onError,
  onFileSelect,
  acceptedFileTypes = [".csv", ".xlsx", ".xls", ".pdf"],
  showPreview = true,
  showFileViewer = true,
  showFileViewerHeader = true,
  viewerOnly = false,
  className = "",
  children,
  selectedFile: externalSelectedFile,
  autoProcess = false,
  onSheetsParsed,
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [parsedData, setParsedData] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [showViewer, setShowViewer] = useState(false);

  // File viewer states
  const [fileType, setFileType] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [excelSheets, setExcelSheets] = useState([]);
  const [extractedText, setExtractedText] = useState(null);
  const [hasExcelData, setHasExcelData] = useState(false);

  /**
   * Get parser configuration based on app type and file type
   */
  const getParserConfig = useCallback(() => {
    // If config is provided, use it
    if (config && Object.keys(config).length > 0) {
      return config;
    }

    // Otherwise, use preset configs based on app type and file type
    let selectedConfig = null;
    if (appType === "trucking") {
      if (fileType === "pdf") {
        selectedConfig = PRESET_CONFIGS.PDF_TRUCKING_LOAD;
      } else {
        selectedConfig = PRESET_CONFIGS.TRUCKING_LOAD;
      }
    } else {
      if (fileType === "pdf") {
        selectedConfig = PRESET_CONFIGS.PDF_SERVICE_JOB;
      } else {
        selectedConfig = PRESET_CONFIGS.SERVICE_JOBS;
      }
    }
    return selectedConfig;
  }, [config, appType, fileType]);

  /**
   * Determine file type from file extension
   */
  const determineFileType = useCallback((file) => {
    const extension = file.name.split(".").pop().toLowerCase();

    let detectedType = null;
    if (extension === "pdf") {
      detectedType = "pdf";
    } else if (["xlsx", "xls"].includes(extension)) {
      detectedType = "excel";
    } else if (extension === "csv") {
      detectedType = "csv";
    } else if (["jpg", "jpeg", "png", "gif"].includes(extension)) {
      detectedType = "image";
    }

    return detectedType;
  }, []);

  /**
   * Handle file selection
   */
  const handleFileSelect = useCallback(
    (event) => {
      const file = event.target.files[0];
      if (!file) return;

      setSelectedFile(file);
      setParseError(null);
      setShowViewer(false);

      // Determine file type
      const detectedFileType = determineFileType(file);
      setFileType(detectedFileType);

      // Call external callback if provided
      onFileSelect?.(file);

      // The specialized parsers will handle parsing automatically when rendered
    },
    [onFileSelect, determineFileType]
  );

  /**
   * Parse the selected file using appropriate parser
   */
  const handleFileParse = useCallback(
    (file = selectedFile, type = fileType) => {
      if (!file || !type) return;

      // The specialized parsers handle parsing automatically when rendered
      // This function is mainly for manual triggering if needed
    },
    [selectedFile, fileType]
  );

  /**
   * Handle successful parsing
   */
  const handleParseSuccess = useCallback(
    (extractedData, rawData) => {
      setIsLoading(false);
      setParsedData(extractedData);

      // Call external callback
      onDataParsed?.(extractedData, rawData);

      // For non-Excel files, show viewer immediately if enabled
      if (showFileViewer && selectedFile && fileType !== "excel") {
        setShowViewer(true);
      }
    },
    [onDataParsed, showFileViewer, selectedFile, fileType]
  );

  /**
   * Handle parsing errors
   */
  const handleParseError = useCallback(
    (error) => {
      setIsLoading(false);
      setParseError(error.message);

      // Call external callback
      onError?.(error);
    },
    [onError, fileType, selectedFile]
  );

  /**
   * Clear selected file and data
   */
  const handleClear = useCallback(() => {
    setSelectedFile(null);
    setParsedData([]);
    setParseError(null);
    setShowViewer(false);
    setFileType(null);
    setFileData(null);
    setPdfUrl(null);
    setExcelSheets([]);
    setExtractedText(null);
    setHasExcelData(false);

    // Clear file input
    const fileInput = document.getElementById("file-processor-input");
    if (fileInput) {
      fileInput.value = "";
    }

    // Clean up PDF URL
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }
  }, [pdfUrl]);

  /**
   * Toggle file viewer
   */
  const toggleViewer = useCallback(() => {
    setShowViewer(!showViewer);
  }, [showViewer]);

  /**
   * Handle PDF text extraction for viewer
   */
  const handlePDFTextExtracted = useCallback((text) => {
    setExtractedText(text);
  }, []);

  // Handle external selected file
  useEffect(() => {
    if (externalSelectedFile && autoProcess) {
      setSelectedFile(externalSelectedFile);
      setParseError(null);
      setShowViewer(false);

      // Determine file type
      const detectedFileType = determineFileType(externalSelectedFile);
      setFileType(detectedFileType);

      // Call external callback if provided
      onFileSelect?.(externalSelectedFile);

      // Auto-show viewer for supported file types or in viewerOnly mode
      if (
        showFileViewer &&
        (viewerOnly ||
          (detectedFileType && ["excel", "pdf"].includes(detectedFileType)))
      ) {
        setShowViewer(true);
      }
    }
  }, [
    externalSelectedFile,
    autoProcess,
    determineFileType,
    onFileSelect,
    showFileViewer,
    viewerOnly,
  ]);

  // Show viewer when Excel data becomes available with proper state synchronization
  useEffect(() => {
    // Check if we should show the viewer for Excel files
    if (
      showFileViewer &&
      selectedFile &&
      fileType === "excel" &&
      !showViewer &&
      (hasExcelData ||
        (Array.isArray(excelSheets) &&
          excelSheets.length > 0 &&
          excelSheets[0]?.data &&
          Array.isArray(excelSheets[0].data) &&
          excelSheets[0].data.length > 0))
    ) {
      setShowViewer(true);
    }
  }, [showFileViewer, selectedFile, fileType, hasExcelData]);

  // Show viewer when PDF URL becomes available
  useEffect(() => {
    if (
      showFileViewer &&
      selectedFile &&
      fileType === "pdf" &&
      pdfUrl &&
      !showViewer
    ) {
      setShowViewer(true);
    }
  }, [showFileViewer, selectedFile, fileType, pdfUrl]);

  // Force viewer to show in viewerOnly mode
  useEffect(() => {
    if (viewerOnly && selectedFile && fileType && !showViewer) {
      setShowViewer(true);
    }
  }, [viewerOnly, selectedFile, fileType]);

  /**
   * Render file upload area
   */
  const renderFileUpload = () => (
    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
      <DocumentArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
      <div className="mt-4">
        <label htmlFor="file-processor-input" className="cursor-pointer">
          <span className="mt-2 block text-sm font-medium text-gray-900">
            {selectedFile ? selectedFile.name : "Upload a file"}
          </span>
          <span className="mt-1 block text-xs text-gray-500">
            Supported formats: {acceptedFileTypes.join(", ")}
          </span>
        </label>
        <input
          id="file-processor-input"
          type="file"
          className="sr-only"
          accept={acceptedFileTypes.join(",")}
          onChange={handleFileSelect}
        />
      </div>
      {selectedFile && (
        <div className="mt-4 flex justify-center space-x-2">
          <Button
            size="sm"
            onClick={() => handleFileParse()}
            disabled={isLoading}
          >
            {isLoading ? <Spinner size="sm" /> : "Process File"}
          </Button>
          {showFileViewer && fileType && (
            <Button
              size="sm"
              type="secondary"
              onClick={toggleViewer}
              disabled={isLoading}
            >
              <EyeIcon className="h-4 w-4 mr-1" />
              {showViewer ? "Hide Preview" : "Preview"}
            </Button>
          )}
          <Button
            size="sm"
            type="alternative"
            onClick={handleClear}
            disabled={isLoading}
          >
            <XMarkIcon className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );

  /**
   * Render file viewer
   */
  const renderFileViewer = () => {
    // Basic checks - must have viewer enabled, file selected, and file type determined
    if (!showViewer || !selectedFile || !fileType) {
      return null;
    }

    // For Excel files, check if we have valid sheets data
    if (fileType === "excel") {
      const hasValidExcelData =
        Array.isArray(excelSheets) &&
        excelSheets.length > 0 &&
        excelSheets[0]?.data &&
        Array.isArray(excelSheets[0].data) &&
        excelSheets[0].data.length > 0;

      if (!hasValidExcelData) {
        return null;
      }
    }

    // For PDF files, check if we have PDF URL
    if (fileType === "pdf" && !pdfUrl) {
      return null;
    }

    // For image files, check if we have file data
    if (fileType === "image" && !fileData) {
      return null;
    }

    return (
      <div className={showFileViewerHeader ? "mt-6" : ""}>
        {showFileViewerHeader && (
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">File Preview</h3>
            <Button size="sm" type="alternative" onClick={toggleViewer}>
              <XMarkIcon className="h-4 w-4" />
            </Button>
          </div>
        )}
        <div
          className={
            showFileViewerHeader
              ? "border border-gray-200 rounded-lg overflow-hidden"
              : "h-full"
          }
          style={{ height: showFileViewerHeader ? "400px" : "100%" }}
        >
          <FileViewer
            fileType={fileType}
            fileData={fileData}
            pdfUrl={pdfUrl}
            excelSheets={excelSheets}
            sheetData={excelSheets}
            onPDFTextExtracted={handlePDFTextExtracted}
            viewerStyle={{ height: "100%", width: "100%" }}
          />
        </div>
      </div>
    );
  };

  /**
   * Render specialized parser based on file type
   */
  const renderSpecializedParser = () => {
    if (!selectedFile || !fileType) return null;

    const commonProps = {
      file: selectedFile,
      config: getParserConfig(),
      onDataParsed: handleParseSuccess,
      onError: handleParseError,
      onFileDataUpdate: (data) => {
        setFileData(data);
      },
      onPdfUrlUpdate: (url) => {
        setPdfUrl(url);
      },
      onExcelSheetsUpdate: (sheets) => {
        // Validate the sheets data structure
        const isValidSheetsData =
          Array.isArray(sheets) &&
          sheets.length > 0 &&
          sheets[0]?.data &&
          Array.isArray(sheets[0].data) &&
          sheets[0].data.length > 0;

        // Only update if the data has actually changed
        if (JSON.stringify(excelSheets) !== JSON.stringify(sheets)) {
          setExcelSheets(sheets);
        }

        if (hasExcelData !== isValidSheetsData) {
          setHasExcelData(isValidSheetsData);
        }
        if (isValidSheetsData) {
          onSheetsParsed?.(sheets);
        }
      },
      onExtractedTextUpdate: (text) => {
        setExtractedText(text);
      },
    };

    switch (fileType) {
      case "csv":
        return <CSVParser {...commonProps} />;
      case "excel":
        return <ExcelParser {...commonProps} />;
      case "pdf":
        return <PDFReader {...commonProps} />;
      default:
        return null;
    }
  };

  /**
   * Render data preview
   */
  const renderDataPreview = () => {
    if (!showPreview || parsedData.length === 0) return null;

    return (
      <div className="mt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Parsed Data Preview ({parsedData.length} records)
        </h3>
        <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-auto">
          <pre className="text-sm text-gray-700">
            {JSON.stringify(parsedData.slice(0, 3), null, 2)}
          </pre>
          {parsedData.length > 3 && (
            <p className="text-sm text-gray-500 mt-2">
              ... and {parsedData.length - 3} more records
            </p>
          )}
        </div>
      </div>
    );
  };

  /**
   * Render extracted text preview (for PDFs)
   */
  const renderTextPreview = () => {
    if (!extractedText || fileType !== "pdf") return null;

    return (
      <div className="mt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Extracted Text Preview
        </h3>
        <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-auto">
          <pre className="text-sm text-gray-700 whitespace-pre-wrap">
            {extractedText.slice(0, 1000)}
            {extractedText.length > 1000 && "..."}
          </pre>
        </div>
      </div>
    );
  };

  /**
   * Render error message
   */
  const renderError = () => {
    if (!parseError) return null;

    return (
      <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex">
          <div className="flex-shrink-0">
            <XMarkIcon className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Processing Error
            </h3>
            <div className="mt-2 text-sm text-red-700">{parseError}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`file-processor ${className}`} {...props}>
      {viewerOnly ? (
        // Only render the FileViewer when in viewer-only mode
        <>
          {renderFileViewer()}
          {renderSpecializedParser()}
        </>
      ) : (
        // Render the full interface when not in viewer-only mode
        <>
          {renderFileUpload()}
          {renderError()}
          {renderFileViewer()}
          {renderSpecializedParser()}
          {renderDataPreview()}
          {renderTextPreview()}

          {/* Custom children render */}
          {children && typeof children === "function"
            ? children({
                parsedData,
                isLoading,
                error: parseError,
                selectedFile,
                fileType,
                extractedText,
                showViewer,
                toggleViewer,
              })
            : children}
        </>
      )}
    </div>
  );
};

export default FileProcessor;
